<template>
  <div>
    <div v-if="image != null"
         class="form">
      <label for="width_in_inches">Width (in): <input id="width_in_inches" type="number" v-model="widthInInches"></label>
      <label for="height_in_inches">Height (in): <input id="height_in_inches" type="number" v-model="heightInInches"></label>
      <button @click="() => { downloadImage(); }">Download image</button>
      <label class="upload">
        Upload another map
        <input type="file" @change="(e) => { handleFileUpload(e); }">
      </label>
    </div>
    <div class="map">
      <canvas id="canvas"
              :style="canvasStyle"
              v-if="image != null"
              :class="{ portrait: isPortrait }"
              @mousemove="updateCrosshair"
              @mouseenter="() => { isDrawingCrosshair = true; updateCrosshair(); }"
              @mouseleave="() => { isDrawingCrosshair = false; updateCrosshair(); }"></canvas>
      <label v-else="" for="file_upload">
        <input type="file" id="file_upload" @change="(e) => handleFileUpload(e)">
        Click to upload a map</label>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Main',
  props: {
    msg: String,
  },
  data() {
    return {
      isDrawingCrosshair: false,
      isPortrait: false,
      image: null,
      heightInInches: 48,
      widthInInches: 72,
      windowWidth: 1,
      windowHeight: 1,
    };
  },
  computed: {
    pixelPerInch() {
      const targetSize = 1500;
      return Math.max(targetSize / this.widthInInches, targetSize / this.heightInInches);
    },
    canvasStyle() {
      const availableRatio = this.windowWidth / this.windowHeight;
      const ratio = this.widthInInches / this.heightInInches;
      if (ratio >= availableRatio) {
        return `width: 90vw; height: ${90 / ratio}vw`;
      } else {
        return `width: ${85 * ratio}vh; height: 85vh`;
      }
    },
  },
  methods: {
    drawImageAndGrid() {
      const canvas = document.getElementById('canvas');
      if (canvas == null || this.widthInInches === 0 || this.heightInInches === 0) {
        return;
      }
      const canvasWidth = this.widthInInches * this.pixelPerInch;
      const canvasHeight = this.heightInInches * this.pixelPerInch;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(this.image, 0, 0, canvasWidth, canvasHeight);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#ffffff66';
      // ctx.setLineDash([3,3]);
      ctx.beginPath();
      const xInch = canvasWidth / this.widthInInches;
      const yInch = canvasHeight / this.heightInInches;
      for (let x = 1; x < this.widthInInches; x += 1) {
        ctx.moveTo(x * xInch, 0);
        ctx.lineTo(x * xInch, canvasHeight);
      }
      for (let y = 1; y < this.heightInInches; y += 1) {
        ctx.moveTo(0, y * yInch);
        ctx.lineTo(canvasWidth, y * yInch);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'red';
      for (let x = 12; x < this.widthInInches; x += 12) {
        ctx.moveTo(x * xInch, 0);
        ctx.lineTo(x * xInch, canvasHeight);
      }
      for (let y = 12; y < this.heightInInches; y += 12) {
        ctx.moveTo(0, y * yInch);
        ctx.lineTo(canvasWidth, y * yInch);
      }
      ctx.closePath();
      ctx.stroke();
    },
    updateCrosshair(e) {
      this.drawImageAndGrid();
      if (!this.isDrawingCrosshair || e == null) {
        return;
      }
      const canvas = document.getElementById('canvas');
      const ctx = canvas.getContext('2d');
      const bounds = e.target.getBoundingClientRect();
      const relX = e.pageX - bounds.left;
      const relY = e.pageY - bounds.top;
      const percentOfWidth = relX / parseInt(bounds.right - bounds.left, 10);
      const percentOfHeight = relY / parseInt(bounds.bottom - bounds.top, 10);
      ctx.strokeStyle = 'cyan';
      ctx.lineWidth = 3;
      ctx.beginPath();
      const fractionOfAnInch = 2;
      const xStep = (canvas.width / this.widthInInches) / fractionOfAnInch;
      const yStep = (canvas.height / this.heightInInches) / fractionOfAnInch;
      const xInch = Math.round(canvas.width * percentOfWidth / xStep) / fractionOfAnInch;
      const yInch = Math.round(canvas.height * percentOfHeight / yStep) / fractionOfAnInch;
      const x = xInch * xStep * fractionOfAnInch;
      const y = yInch * yStep * fractionOfAnInch;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.closePath();
      ctx.stroke();
      ctx.font = `${canvas.width / canvas.clientWidth * 16}px sans-serif`;
      const xText = `${xInch}"`;
      const text = `${xText} x ${yInch}"`;
      const textWidth = ctx.measureText(text);
      const xOffset = xInch > 36 ? -(xStep * fractionOfAnInch + textWidth.width) : xStep * fractionOfAnInch;
      const yOffset = yInch > 24 ? -yStep * fractionOfAnInch : 2 * fractionOfAnInch * yStep;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowColor = 'rgba(0,0,0,0.9)';
      ctx.shadowBlur = 20;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.fillText(text, x + xOffset, y + yOffset);
      ctx.closePath();
    },
    toRatio(fraction) {
      switch (fraction) {
        case 0.25:
          return '1/4';
        case 0.5:
          return '1/2';
        case 0.75:
          return '3/4';
        default:
          return fraction;
      }
    },
    handleFileUpload(e) {
      const vue = this;
      if (e.target.files && e.target.files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageData = e.target.result;
          const image = new Image();
          image.onload = () => {
            vue.image = image;
            setTimeout(() => {
              vue.drawImageAndGrid();
            }, 100);
          };
          image.src = imageData;
        };

        reader.readAsDataURL(e.target.files[0]); // convert to base64 string
      }
    },
    downloadImage() {
      var link = document.createElement('a');
      link.download = 'map.jpg';
      link.href = document.getElementById('canvas').toDataURL('image/jpeg');
      link.click();
    },
  },
  watch: {
    widthInInches() {
      this.drawImageAndGrid();
    },
    heightInInches() {
      this.drawImageAndGrid();
    },
  },
  mounted() {
    // const image = new Image();
    // image.onload = () => {
    //   this.image = image;
    //   this.drawImageAndGrid();
    // };
    // image.src = 'assets/map.png';

    this.isPortrait = (window.innerWidth / window.innerHeight) > 1.5;

    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;

    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
      this.isPortrait = (window.innerWidth / window.innerHeight) > 1.5;
    });
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>

.form {
  padding: 12px 16px;
  border-radius: 3px;
  margin: 16px;
  background: #ffffffee;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5vw 5vw #00000066;

  label {
    margin-right: 40px;
    font-size: 14px;

    input[type=number] {
      margin-left: 8px;
      width: 50px;
      padding: 3px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;

    }

    &.upload {
      input {
        opacity: 0;
        width: 0px;
        height: 0px;
        display: none;
      }
    }
  }

  .upload,
  button {
    font-size: 14px;
    padding: 6px 10px;
    outline: none;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    transition: all .2s ease-in-out;
    background: #0f67a2;
    color: #ffffffee;
    display: flex;
    align-items: center;
    margin-right: 16px;
  }

  .upload {
    margin-right: 0;
  }
}


.map {
  label {
    background: #eee;
    position: fixed;
    top: 50%;
    left: 50%;
    font-size: 3vw;
    padding: 16px;
    width: 50vw;
    height: 20vh;
    margin-left: -25vw;
    margin-top: -10vh;
    border-radius: 1vw;
    border: solid 2px #111;
    box-shadow: 0 5vw 5vw #00000066;
    outline: none;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;
    transition: all .2s ease-in-out;

    &:hover,
    &:active {
      background: #fff;
    }

    input {
      opacity: 0;
      width: 0px;
      height: 0px;
    }
  }
}
</style>
